/**
 * Top-of-page aggregates for the Execution Ledger monitor.
 *
 * COMPACT layout (2026-06-23 refactor — the prior StatCard + tall chart
 * + TopOrgs-below was eating too much vertical room, leaving only ~1
 * list row visible). Three rows total:
 *   - inline metric chips (label · value × 5) — single ~28px row
 *   - collapsible AreaChart (default expanded, height 100) — toggle
 *     button frees ~115px when collapsed
 *   - bucket toggle is co-located with the chart header
 *
 * Top Orgs is no longer rendered here — it moved to the master/detail
 * grid's right column (rendered by `ExecutionLedger.tsx` only when no
 * execution is selected; the detail pane takes over otherwise). See
 * `ExecutionLedgerTopOrgs.tsx`.
 *
 * Driven by `GET /api/platform/executions/stats` over the SAME filter
 * window the list uses. Bucket defaults to `day`; toggle switches to
 * `hour` for narrow windows.
 */

import { Activity, ChevronDown, ChevronRight } from "lucide-react";
import { useState } from "react";
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import type { ExecutionLedgerStatsResult } from "@/types/execution-ledger";

interface Props {
  stats: ExecutionLedgerStatsResult | null;
  loading: boolean;
  bucket: "hour" | "day";
  onBucketChange: (bucket: "hour" | "day") => void;
}

export function ExecutionLedgerSummary({
  stats,
  loading,
  bucket,
  onBucketChange,
}: Props) {
  const [chartOpen, setChartOpen] = useState(true);

  const t = stats?.totals;
  const series = stats?.series ?? [];

  const totalCount = t?.count ?? 0;
  // "Errors" rolls up failed + awaiting_user_timeout (the human-gate
  // expiry is a documented error class per mission gating plan §A.3;
  // the detail pane discriminates via error.code).
  const errorCount =
    (t?.byStatus?.failed ?? 0) + (t?.byStatus?.awaiting_user_timeout ?? 0);
  const errorRate =
    totalCount > 0 ? `${Math.round((errorCount / totalCount) * 100)}%` : "0%";

  const v = (n: string) => (loading ? "…" : n);

  return (
    <div
      className="flex flex-col gap-2"
      data-testid="execution-ledger-summary"
    >
      {/* Single-row inline chips — much shorter than the prior StatCards
          (5 chips × ~6ch label + ~6ch value, total ~28px tall). */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-900">
        <Chip label="Executions" value={v(fmtCount(totalCount))} />
        <Chip
          label="Missions"
          value={v(fmtCount(t?.byKind?.mission ?? 0))}
        />
        <Chip
          label="Errors"
          value={v(`${fmtCount(errorCount)} (${errorRate})`)}
          tone={errorCount > 0 ? "rose" : undefined}
        />
        <Chip label="Cost" value={v(fmtCost(t?.costUsd ?? 0))} />
        <Chip
          label="Tokens"
          value={v(fmtCount((t?.tokensIn ?? 0) + (t?.tokensOut ?? 0)))}
        />
        <button
          type="button"
          onClick={() => setChartOpen((o) => !o)}
          className="ml-auto inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
          data-testid="execution-ledger-chart-toggle"
          title={chartOpen ? "Hide chart" : "Show chart"}
        >
          {chartOpen ? (
            <ChevronDown className="h-3 w-3" />
          ) : (
            <ChevronRight className="h-3 w-3" />
          )}
          Chart
        </button>
      </div>

      {chartOpen && series.length > 0 && (
        <div className="rounded-md border border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-900">
          <div className="mb-1 flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
            <Activity className="h-3.5 w-3.5" />
            Executions per bucket (errors overlay)
            <div
              className="ml-auto inline-flex overflow-hidden rounded border border-slate-200 text-[10px] dark:border-slate-700"
              role="group"
              aria-label="Bucket size"
            >
              <BucketButton
                active={bucket === "hour"}
                onClick={() => onBucketChange("hour")}
                testid="execution-ledger-bucket-hour"
              >
                hour
              </BucketButton>
              <BucketButton
                active={bucket === "day"}
                onClick={() => onBucketChange("day")}
                testid="execution-ledger-bucket-day"
              >
                day
              </BucketButton>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={100}>
            <AreaChart
              data={series}
              margin={{ top: 5, right: 10, left: 0, bottom: 0 }}
            >
              <XAxis
                dataKey="ts"
                tickFormatter={(ts) => fmtTick(ts, bucket)}
                stroke="currentColor"
                fontSize={10}
                tick={{ fill: "currentColor" }}
              />
              <YAxis
                stroke="currentColor"
                fontSize={10}
                tick={{ fill: "currentColor" }}
                width={28}
              />
              <Tooltip
                formatter={(value: number, name: string) => [value, name]}
                labelFormatter={(label: number) =>
                  new Date(label).toLocaleString()
                }
                contentStyle={{ fontSize: "12px" }}
              />
              <Area
                type="monotone"
                dataKey="count"
                name="executions"
                stroke="hsl(220, 80%, 55%)"
                fill="hsl(220, 80%, 55%)"
                fillOpacity={0.25}
              />
              <Area
                type="monotone"
                dataKey="errors"
                name="errors"
                stroke="hsl(0, 80%, 55%)"
                fill="hsl(0, 80%, 55%)"
                fillOpacity={0.4}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function Chip({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "rose";
}) {
  const valueCls =
    tone === "rose"
      ? "font-semibold text-rose-700 dark:text-rose-300"
      : "font-semibold text-slate-900 dark:text-slate-100";
  return (
    <span className="flex items-baseline gap-1.5">
      <span className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </span>
      <span className={valueCls}>{value}</span>
    </span>
  );
}

function BucketButton({
  active,
  onClick,
  children,
  testid,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  testid: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-1.5 py-0.5 uppercase ${
        active
          ? "bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300"
          : "text-slate-500 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-800"
      }`}
      data-testid={testid}
    >
      {children}
    </button>
  );
}

function fmtCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

function fmtCost(usd: number): string {
  if (usd === 0) return "$0";
  if (usd < 0.01) return `$${usd.toFixed(4)}`;
  return `$${usd.toFixed(2)}`;
}

function fmtTick(ts: number, bucket: "hour" | "day"): string {
  const d = new Date(ts);
  if (bucket === "day") {
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  }
  return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}
